import { useState, useMemo, useRef, useEffect, ReactNode } from "react";
import { ActivePage, ChartTab, DataPoint, DateRange, DualPoint } from "./types";
import { C, CONTENT_ITEMS, GEO_DATA, INVOICE_AGING, PRODUCT_ROWS, QUOTE_PIPELINE, RECENT_ACTIVITY } from "./constants";

// ─── Seeded random ────────────────────────────────────────────────────────────

function sr(seed: number) {
  let s = seed;
  return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
}

// ─── Data generators ──────────────────────────────────────────────────────────

function genRevenue(range: DateRange): DataPoint[] {
  const r = sr(42);
  const cfg: Record<DateRange, { n: number; base: number; lbl: (i: number) => string }> = {
    "7d":  { n: 7,  base: 4200,  lbl: i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i] },
    "30d": { n: 30, base: 3800,  lbl: i => `${i+1}` },
    "90d": { n: 13, base: 15000, lbl: i => `W${i+1}` },
    "12m": { n: 12, base: 62000, lbl: i => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i] },
  };
  const c = cfg[range]; let val = c.base;
  return Array.from({ length: c.n }, (_, i) => {
    val = Math.max(val * (0.85 + r() * 0.32), 800);
    return { label: c.lbl(i), value: Math.round(val) };
  });
}

function genPrevRevenue(range: DateRange): DataPoint[] {
  const r = sr(99);
  const cfg: Record<DateRange, { n: number; base: number; lbl: (i: number) => string }> = {
    "7d":  { n: 7,  base: 3600,  lbl: i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i] },
    "30d": { n: 30, base: 3200,  lbl: i => `${i+1}` },
    "90d": { n: 13, base: 12000, lbl: i => `W${i+1}` },
    "12m": { n: 12, base: 54000, lbl: i => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i] },
  };
  const c = cfg[range]; let val = c.base;
  return Array.from({ length: c.n }, (_, i) => {
    val = Math.max(val * (0.84 + r() * 0.30), 600);
    return { label: c.lbl(i), value: Math.round(val) };
  });
}

function genQuotes(range: DateRange): DualPoint[] {
  const r = sr(77);
  const cfg: Record<DateRange, { n: number; lbl: (i: number) => string }> = {
    "7d":  { n: 7,  lbl: i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i] },
    "30d": { n: 30, lbl: i => `${i+1}` },
    "90d": { n: 13, lbl: i => `W${i+1}` },
    "12m": { n: 12, lbl: i => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i] },
  };
  const c = cfg[range];
  return Array.from({ length: c.n }, (_, i) => ({
    label: c.lbl(i), a: Math.round(1 + r() * 6), b: Math.round(r() * 4),
  }));
}

function genInvoices(range: DateRange): DualPoint[] {
  const r = sr(55);
  const cfg: Record<DateRange, { n: number; lbl: (i: number) => string }> = {
    "7d":  { n: 7,  lbl: i => ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i] },
    "30d": { n: 30, lbl: i => `${i+1}` },
    "90d": { n: 13, lbl: i => `W${i+1}` },
    "12m": { n: 12, lbl: i => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i] },
  };
  const c = cfg[range];
  return Array.from({ length: c.n }, (_, i) => ({
    label: c.lbl(i),
    a: Math.round(2000 + r() * 12000),  // issued
    b: Math.round(1000 + r() * 10000),  // paid
  }));
}

// ─── Static datasets ──────────────────────────────────────────────────────────



// ─── KPI helpers ──────────────────────────────────────────────────────────────

function getKPIs(range: DateRange) {
  const m: Record<DateRange, number> = { "7d":1, "30d":4.3, "90d":13, "12m":52 };
  const f = m[range] / 52;
  return {
    revenue:      { value: Math.round(83700 * f + 4200),   prev: Math.round(72100 * f + 3600), delta: +14.2 },
    quotes:       { value: Math.round(15 * f + 2),         prev: Math.round(13 * f + 1),       delta: +8.7  },
    invoices:     { value: Math.round(15 * f + 1),         prev: Math.round(14 * f + 1),       delta: +6.3  },
    avgDeal:      { value: 5580,                            prev: 5360,                          delta: +4.1  },
    convRate:     { value: 20,                              prev: 22,                            delta: -2.1  },
    outstanding:  { value: 117510,                          prev: 98400,                         delta: +19.4 },
  };
}

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtCurrency(v: number, short = false): string {
  if (short) {
    if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v/1e3).toFixed(1)}K`;
    return `$${v}`;
  }
  return new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", maximumFractionDigits:0 }).format(v);
}
function fmtNum(v: number): string { return v.toLocaleString(); }
function pct(a: number, b: number): number { return b ? Math.round((a/b)*100) : 0; }
// function delta(v: number, p: number): number { return p ? Math.round(((v-p)/p)*100*10)/10 : 0; }

// ─── SVG Charts ───────────────────────────────────────────────────────────────

function Sparkline({ data, color, h = 28, w = 72 }: { data: number[]; color: string; h?: number; w?: number }) {
  if (data.length < 2) return null;
  const max = Math.max(...data); const min = Math.min(...data); const rng = max - min || 1;
  const pts = data.map((v, i) => `${(i/(data.length-1))*w},${h-((v-min)/rng)*h}`);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow:"visible" }}>
      <defs>
        <linearGradient id={`sl${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={`M ${pts.join(" L ")} L ${w},${h} L 0,${h} Z`} fill={`url(#sl${color.slice(1)})`}/>
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function AreaChart({ data, prev, color, prevColor, h = 160 }: {
  data: DataPoint[]; prev?: DataPoint[]; color: string; prevColor?: string; h?: number;
}) {
  const [hovIdx, setHovIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const W = 100;
  const allVals = [...data.map(d=>d.value), ...(prev||[]).map(d=>d.value)];
  const max = Math.max(...allVals) * 1.12 || 1;

  const pts = (d: DataPoint[]) => d.map((p, i) => ({
    x: (i/(d.length-1))*W, y: h - (p.value/max)*h*0.88 - h*0.04,
  }));

  const line  = (p: {x:number;y:number}[]) => "M "+p.map(pt=>`${pt.x},${pt.y}`).join(" L ");
  const area  = (p: {x:number;y:number}[], d: DataPoint[]) =>
    line(p) + ` L ${p[p.length-1].x},${h} L ${p[0].x},${h} Z`;

  const mainPts = pts(data);
  const prevPts = prev ? pts(prev) : [];
  const gMain = `ag${color.slice(1)}`;
  const gPrev = `agp${color.slice(1)}`;

  return (
    <div style={{ position:"relative" }}>
      <svg ref={svgRef} viewBox={`0 0 100 ${h}`} width="100%" height={h} preserveAspectRatio="none"
        style={{ cursor:"crosshair" }}
        onMouseMove={e => {
          const r = svgRef.current!.getBoundingClientRect();
          const x = ((e.clientX - r.left)/r.width)*100;
          setHovIdx(Math.max(0, Math.min(data.length-1, Math.round((x/100)*(data.length-1)))));
        }}
        onMouseLeave={() => setHovIdx(null)}>
        <defs>
          <linearGradient id={gMain} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.20"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
          {prev && <linearGradient id={gPrev} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={prevColor||"#fff"} stopOpacity="0.07"/>
            <stop offset="100%" stopColor={prevColor||"#fff"} stopOpacity="0"/>
          </linearGradient>}
        </defs>
        {[0.25,0.5,0.75,1].map(t => (
          <line key={t} x1="0" y1={h*(1-t)*0.88+h*0.04} x2="100" y2={h*(1-t)*0.88+h*0.04}
            stroke={C.grid} strokeWidth="0.4"/>
        ))}
        {prev && prevPts.length > 1 && <>
          <path d={area(prevPts, prev)} fill={`url(#${gPrev})`}/>
          <path d={line(prevPts)} fill="none" stroke={prevColor||"rgba(255,255,255,0.2)"} strokeWidth="0.6" strokeDasharray="1.5,1" strokeLinecap="round"/>
        </>}
        {mainPts.length > 1 && <>
          <path d={area(mainPts, data)} fill={`url(#${gMain})`}/>
          <path d={line(mainPts)} fill="none" stroke={color} strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round"/>
        </>}
        {hovIdx !== null && mainPts[hovIdx] && (
          <>
            <line x1={mainPts[hovIdx].x} y1="0" x2={mainPts[hovIdx].x} y2={h}
              stroke={color} strokeWidth="0.4" strokeDasharray="1.2,1" opacity="0.6"/>
            <circle cx={mainPts[hovIdx].x} cy={mainPts[hovIdx].y} r="1.4" fill={color}/>
            {prev && prevPts[hovIdx] && <circle cx={prevPts[hovIdx].x} cy={prevPts[hovIdx].y} r="1" fill={prevColor||"#fff"} opacity="0.5"/>}
          </>
        )}
      </svg>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
        {data.filter((_,i) => data.length<=12 || i%(Math.ceil(data.length/8))===0 || i===data.length-1)
          .map((d,i) => <span key={i} style={{ fontSize:9, color:C.text3 }}>{d.label}</span>)}
      </div>
      {hovIdx !== null && (
        <div style={{
          position:"absolute", top:4,
          left:`calc(${(hovIdx/(data.length-1))*100}% + 6px)`,
          transform: hovIdx > data.length/2 ? "translateX(calc(-100% - 12px))" : "none",
          background:"rgba(22,22,20,0.96)", border:"1px solid rgba(255,255,255,0.12)",
          borderRadius:8, padding:"7px 11px", pointerEvents:"none",
          boxShadow:"0 4px 20px rgba(0,0,0,0.5)", zIndex:10, minWidth:100,
        }}>
          <div style={{ fontSize:10, color:C.text3, marginBottom:4 }}>{data[hovIdx].label}</div>
          <div style={{ fontSize:14, fontWeight:700, color:color }}>
            {data[hovIdx].value > 999 ? fmtCurrency(data[hovIdx].value, true) : data[hovIdx].value}
          </div>
          {prev?.[hovIdx] && (
            <div style={{ fontSize:10, color:C.text3, marginTop:2 }}>
              vs {prev[hovIdx].value > 999 ? fmtCurrency(prev[hovIdx].value, true) : prev[hovIdx].value} prev
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BarChart({ data, colorA, colorB, labelA, labelB, h = 160, valueType = "currency" }: {
  data: DualPoint[]; colorA: string; colorB: string; labelA: string; labelB: string;
  h?: number; valueType?: "currency" | "number";
}) {
  const [hovIdx, setHovIdx] = useState<number | null>(null);
  const maxV = Math.max(...data.flatMap(d => [d.a, d.b])) || 1;
  const bW = Math.max(1.2, 24 / data.length);
  const gap = 0.5;

  return (
    <div style={{ position:"relative" }}>
      <svg viewBox={`0 0 100 ${h}`} width="100%" height={h} preserveAspectRatio="none"
        style={{ cursor:"crosshair" }}
        onMouseMove={e => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const x    = ((e.clientX - rect.left)/rect.width)*100;
          setHovIdx(Math.max(0, Math.min(data.length-1, Math.round((x/100)*(data.length-1)))));
        }}
        onMouseLeave={() => setHovIdx(null)}>
        {[0.25,0.5,0.75,1].map(t => (
          <line key={t} x1="0" y1={h*(1-t)*0.88+h*0.04} x2="100" y2={h*(1-t)*0.88+h*0.04}
            stroke={C.grid} strokeWidth="0.4"/>
        ))}
        {data.map((d, i) => {
          const cx = (i/(data.length-1))*100;
          const hA = (d.a/maxV)*h*0.88; const hB = (d.b/maxV)*h*0.88;
          const isH = hovIdx === i;
          return (
            <g key={i}>
              <rect x={cx-bW-gap/2} y={h-hA} width={bW} height={hA} rx="0.6"
                fill={colorA} opacity={isH?1:0.72} style={{ transition:"opacity 0.15s" }}/>
              <rect x={cx+gap/2} y={h-hB} width={bW} height={hB} rx="0.6"
                fill={colorB} opacity={isH?1:0.72} style={{ transition:"opacity 0.15s" }}/>
            </g>
          );
        })}
      </svg>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
        {data.filter((_,i) => data.length<=12 || i%(Math.ceil(data.length/8))===0 || i===data.length-1)
          .map((d,i) => <span key={i} style={{ fontSize:9, color:C.text3 }}>{d.label}</span>)}
      </div>
      {hovIdx !== null && (
        <div style={{
          position:"absolute", top:4,
          left:`calc(${(hovIdx/(data.length-1))*100}% + 6px)`,
          transform: hovIdx > data.length/2 ? "translateX(calc(-100% - 12px))" : "none",
          background:"rgba(22,22,20,0.96)", border:"1px solid rgba(255,255,255,0.12)",
          borderRadius:8, padding:"7px 11px", pointerEvents:"none",
          boxShadow:"0 4px 20px rgba(0,0,0,0.5)", zIndex:10,
        }}>
          <div style={{ fontSize:10, color:C.text3, marginBottom:5 }}>{data[hovIdx].label}</div>
          <div style={{ display:"flex", gap:12 }}>
            <div>
              <div style={{ fontSize:9, color:colorA, fontWeight:600, marginBottom:2 }}>{labelA}</div>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>
                {valueType==="currency" ? fmtCurrency(data[hovIdx].a, true) : data[hovIdx].a}
              </div>
            </div>
            <div>
              <div style={{ fontSize:9, color:colorB, fontWeight:600, marginBottom:2 }}>{labelB}</div>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>
                {valueType==="currency" ? fmtCurrency(data[hovIdx].b, true) : data[hovIdx].b}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DonutChart({ segments, size = 96, label = "" }: {
  segments: {label:string;value:number;color:string}[]; size?: number; label?: string;
}) {
  const [hov, setHov] = useState<number | null>(null);
  const total = segments.reduce((s,x) => s+x.value, 0) || 1;
  const r = 38; const cx = 50; const cy = 50; let angle = -90;
  const arcs = segments.map(seg => {
    const a1  = (angle*Math.PI)/180;
    const deg = (seg.value/total)*360;
    angle    += deg;
    const a2  = (angle*Math.PI)/180;
    const x1 = cx+r*Math.cos(a1); const y1 = cy+r*Math.sin(a1);
    const x2 = cx+r*Math.cos(a2); const y2 = cy+r*Math.sin(a2);
    return { ...seg, d:`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${deg>180?1:0} 1 ${x2} ${y2} Z` };
  });
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} style={{ overflow:"visible", flexShrink:0 }}>
      {arcs.map((arc, i) => (
        <path key={i} d={arc.d} fill={arc.color}
          opacity={hov===null?0.88:hov===i?1:0.3}
          transform={hov===i?"scale(1.04)":"scale(1)"}
          style={{ transformOrigin:"50px 50px", transition:"all 0.15s", cursor:"pointer" }}
          onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}/>
      ))}
      <circle cx={cx} cy={cy} r={22} fill="var(--surface2,#161614)"/>
      {hov !== null ? (
        <>
          <text x={cx} y={cy-3} textAnchor="middle" fontSize="9" fontWeight="700" fill={arcs[hov].color}>
            {pct(arcs[hov].value, total)}%
          </text>
          <text x={cx} y={cy+8} textAnchor="middle" fontSize="5.5" fill={C.text3}>{arcs[hov].label}</text>
        </>
      ) : (
        <>
          <text x={cx} y={cy-3} textAnchor="middle" fontSize="9" fontWeight="700" fill={C.text}>{total}</text>
          <text x={cx} y={cy+8} textAnchor="middle" fontSize="5.5" fill={C.text3}>{label||"total"}</text>
        </>
      )}
    </svg>
  );
}

function HBar({ value, max, color }: { value: number; max: number; color: string }) {
  return (
    <div style={{ height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
      <div style={{ height:"100%", width:`${pct(value,max)}%`, background:color, borderRadius:2, transition:"width 0.6s ease" }}/>
    </div>
  );
}

// ─── Card primitives ──────────────────────────────────────────────────────────

function Card({ children, style }: { children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ background:"var(--card,rgba(255,255,255,0.025))", border:"1px solid var(--border,rgba(255,255,255,0.08))", borderRadius:14, overflow:"hidden", ...style }}>
      {children}
    </div>
  );
}

function CardHead({ title, sub, right }: { title: string; sub?: string; right?: ReactNode }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid var(--border,rgba(255,255,255,0.08))", background:"rgba(255,255,255,0.01)" }}>
      <div>
        <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{title}</div>
        {sub && <div style={{ fontSize:11, color:C.text3, marginTop:2 }}>{sub}</div>}
      </div>
      {right && <div style={{ flexShrink:0 }}>{right}</div>}
    </div>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, prevValue, dlt, icon, color, spark, prefix = "" }: {
  label:string; value:string; prevValue?:string; dlt:number; icon:string;
  color:string; spark:number[]; prefix?:string;
}) {
  const up = dlt >= 0;
  return (
    <div style={{
      background:"var(--card,rgba(255,255,255,0.025))",
      border:"1px solid var(--border,rgba(255,255,255,0.08))",
      borderRadius:14, padding:"16px 18px",
      display:"flex", flexDirection:"column", gap:10,
      position:"relative", overflow:"hidden",
    }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontSize:10, color:C.text3, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:6 }}>{label}</div>
          <div style={{ fontSize:24, fontWeight:700, color:C.text, letterSpacing:"-0.5px", lineHeight:1 }}>{prefix}{value}</div>
          {prevValue && <div style={{ fontSize:10, color:C.text3, marginTop:4 }}>prev: {prefix}{prevValue}</div>}
        </div>
        <div style={{ width:36, height:36, borderRadius:9, background:`${color}18`, border:`1px solid ${color}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0 }}>{icon}</div>
      </div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
        <span style={{ fontSize:11, fontWeight:600, color:up?"#6ddc6d":C.red }}>
          {up?"↑":"↓"} {Math.abs(dlt)}% vs prev
        </span>
        <Sparkline data={spark} color={color} h={26} w={60}/>
      </div>
    </div>
  );
}

// ─── Date range picker ────────────────────────────────────────────────────────

function DatePicker({ value, onChange }: { value: DateRange; onChange: (v:DateRange)=>void }) {
  return (
    <div style={{ display:"flex", gap:3, background:"var(--card)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, padding:3 }}>
      {(["7d","30d","90d","12m"] as DateRange[]).map(v => (
        <button key={v} onClick={() => onChange(v)} style={{
          fontSize:11, padding:"5px 12px", borderRadius:6, cursor:"pointer",
          fontFamily:"'DM Sans',sans-serif",
          border:`1px solid ${value===v?"rgba(191,61,12,0.35)":"transparent"}`,
          background:value===v?"var(--or-dim,rgba(191,61,12,0.10))":"transparent",
          color:value===v?"var(--or,#BF3D0C)":C.text3,
          fontWeight:value===v?500:400, transition:"all 0.15s",
        }}>{v}</button>
      ))}
    </div>
  );
}

// ─── Page nav ─────────────────────────────────────────────────────────────────

function PageTab({ id, label, active, onClick }: { id:ActivePage; label:string; active:boolean; onClick:()=>void }) {
  return (
    <button onClick={onClick} style={{
      fontSize:12, padding:"7px 16px", borderRadius:8, cursor:"pointer",
      fontFamily:"'DM Sans',sans-serif", fontWeight:active?600:400,
      border:`1px solid ${active?"rgba(191,61,12,0.3)":"transparent"}`,
      background:active?"var(--or-dim)":"transparent",
      color:active?"var(--or)":C.text3, transition:"all 0.15s",
    }}>{label}</button>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Analytics() {
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [chartTab, setChartTab]   = useState<ChartTab>("revenue");
  const [activePage, setActivePage] = useState<ActivePage>("overview");
  const [animKey, setAnimKey]     = useState(0);

  useEffect(() => { setAnimKey(k => k+1); }, [dateRange, activePage]);

  const kpis     = useMemo(() => getKPIs(dateRange),      [dateRange]);
  const revData  = useMemo(() => genRevenue(dateRange),   [dateRange]);
  const prevData = useMemo(() => genPrevRevenue(dateRange),[dateRange]);
  const qData    = useMemo(() => genQuotes(dateRange),    [dateRange]);
  const invData  = useMemo(() => genInvoices(dateRange),  [dateRange]);

  const spark = {
    rev:  useMemo(() => Array.from({length:12},(_,i)=>8000+Math.sin(i*0.7+1)*3000+sr(i+10)()*4000),[]),
    q:    useMemo(() => Array.from({length:12},(_,i)=>Math.round(2+sr(i+20)()*5)),[]),
    inv:  useMemo(() => Array.from({length:12},(_,i)=>Math.round(1+sr(i+30)()*4)),[]),
    deal: useMemo(() => Array.from({length:12},(_,i)=>4200+sr(i+40)()*3000),[]),
    out:  useMemo(() => Array.from({length:12},(_,i)=>90000+sr(i+50)()*60000),[]),
    conv: useMemo(() => Array.from({length:12},(_,i)=>Math.round(15+sr(i+60)()*12)),[]),
  };

  const handleExport = () => {
    const csv = ["Section,Metric,Value",
      `KPIs,Revenue,${kpis.revenue.value}`,
      `KPIs,Quotes,${kpis.quotes.value}`,
      `KPIs,Avg Deal,$${kpis.avgDeal.value}`,
      `KPIs,Conv Rate,${kpis.convRate.value}%`,
      ...PRODUCT_ROWS.map(p=>`Products,${p.name},${p.revenue}`),
    ].join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
    a.download = `aurel-analytics-${dateRange}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const PAGES: { id: ActivePage; label: string }[] = [
    { id:"overview",  label:"Overview"  },
    { id:"revenue",   label:"Revenue"   },
    { id:"products",  label:"Products"  },
    { id:"quotes",    label:"Quotes"    },
    { id:"content",   label:"Content"   },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing:border-box; }
        // :root {
        //   --or:#BF3D0C; --or2:#E8593C; --or-dim:rgba(191,61,12,0.10);
        //   --text:#e8e6de; --text2:#b4b2a9; --text3:#6a6a65;
        //   --border:rgba(255,255,255,0.08); --card:rgba(255,255,255,0.025);
        //   --input-bg:rgba(255,255,255,0.035); --section-header:rgba(255,255,255,0.02);
        //   --surface:#111110; --surface2:#161614;
        // }
        body { margin:0; font-family:'DM Sans',sans-serif; }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:var(--border); border-radius:4px; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        .an-fade { animation:fadeIn 0.3s ease both; }
        .an-up   { animation:fadeUp 0.4s ease both; }
        .an-row:hover { background:rgba(255,255,255,0.025)!important; }
        @media(max-width:1100px){.an-kpi{grid-template-columns:repeat(3,1fr)!important;}}
        @media(max-width:780px) {.an-kpi{grid-template-columns:repeat(2,1fr)!important;} .an-main{grid-template-columns:1fr!important;} .an-bot{grid-template-columns:1fr!important;}}
        @media(max-width:480px) {.an-kpi{grid-template-columns:1fr!important;}}
        select option { background:#1a1917; color:#e8e6de; }
      `}</style>

      <div style={{ minHeight:"100vh", padding:"32px 28px" }}>
        <div style={{ maxWidth:1280, margin:"0 auto" }}>

          {/* ── Page header ── */}
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase", color:"var(--or)", marginBottom:4 }}>Overview</div>
              <h1 style={{ margin:0, fontSize:22, fontWeight:600, color:C.text, letterSpacing:"-0.4px" }}>Analytics</h1>
              <p style={{ margin:"4px 0 0", fontSize:13, color:C.text3 }}>
                Performance across products, sales, quotes, invoices and content.
              </p>
            </div>
            <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
              <DatePicker value={dateRange} onChange={setDateRange}/>
              <button onClick={handleExport} style={{
                display:"flex", alignItems:"center", gap:6, padding:"9px 16px", borderRadius:9,
                border:"1px solid var(--border)", background:"var(--card)",
                color:C.text2, fontSize:12, cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
            </div>
          </div>

          {/* ── Sub-nav ── */}
          <div style={{ display:"flex", gap:4, marginBottom:24, background:"var(--card)", border:"1px solid var(--border)", borderRadius:10, padding:4, overflowX:"auto" }}>
            {PAGES.map(p => (
              <PageTab key={p.id} {...p} active={activePage===p.id} onClick={() => setActivePage(p.id)}/>
            ))}
          </div>

          {/* ────────────────────── OVERVIEW ────────────────────── */}
          {activePage === "overview" && (
            <div className="an-fade" key={`overview-${animKey}`}>
              {/* KPI row */}
              <div className="an-kpi" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
                {[
                  { label:"Revenue",       value:fmtCurrency(kpis.revenue.value,true),   prev:fmtCurrency(kpis.revenue.prev,true),   dlt:kpis.revenue.delta,     icon:"💰", color:C.or,     spark:spark.rev  },
                  { label:"Quotes",        value:String(kpis.quotes.value),               prev:String(kpis.quotes.prev),              dlt:kpis.quotes.delta,      icon:"📋", color:C.blue,   spark:spark.q    },
                  { label:"Invoices",      value:String(kpis.invoices.value),             prev:String(kpis.invoices.prev),            dlt:kpis.invoices.delta,    icon:"🧾", color:C.purple, spark:spark.inv  },
                  { label:"Avg Deal Size", value:fmtCurrency(kpis.avgDeal.value,true),   prev:fmtCurrency(kpis.avgDeal.prev,true),   dlt:kpis.avgDeal.delta,     icon:"📈", color:C.green,  spark:spark.deal },
                  { label:"Conv. Rate",    value:`${kpis.convRate.value}%`,               prev:`${kpis.convRate.prev}%`,              dlt:kpis.convRate.delta,    icon:"🎯", color:C.amber,  spark:spark.conv },
                  { label:"Outstanding",   value:fmtCurrency(kpis.outstanding.value,true),prev:fmtCurrency(kpis.outstanding.prev,true),dlt:kpis.outstanding.delta,icon:"⚠️", color:C.red,    spark:spark.out  },
                ].map((k,i) => (
                  <div key={k.label} className="an-up" style={{ animationDelay:`${i*0.05}s` }}>
                    <KpiCard {...k}/>
                  </div>
                ))}
              </div>

              {/* Main chart + funnel */}
              <div className="an-main" style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:16, marginBottom:16 }}>
                <Card>
                  <CardHead title="Performance"
                    sub="Current period vs previous"
                    right={
                      <div style={{ display:"flex", gap:4 }}>
                        {(["revenue","quotes","invoices"] as ChartTab[]).map(t => (
                          <button key={t} onClick={() => setChartTab(t)} style={{
                            fontSize:11, padding:"5px 11px", borderRadius:7, cursor:"pointer",
                            fontFamily:"'DM Sans',sans-serif", textTransform:"capitalize",
                            border:`1px solid ${chartTab===t?"rgba(191,61,12,0.3)":"transparent"}`,
                            background:chartTab===t?"var(--or-dim)":"transparent",
                            color:chartTab===t?"var(--or)":C.text3, transition:"all 0.15s",
                          }}>{t}</button>
                        ))}
                      </div>
                    }/>
                  <div style={{ padding:"16px 20px 12px" }}>
                    {chartTab==="revenue" && (
                      <div className="an-fade" key="rev">
                        <div style={{ display:"flex", alignItems:"baseline", gap:10, marginBottom:12 }}>
                          <span style={{ fontSize:28, fontWeight:700, color:C.text, letterSpacing:"-0.5px" }}>
                            {fmtCurrency(kpis.revenue.value, true)}
                          </span>
                          <span style={{ fontSize:12, color:"#6ddc6d", fontWeight:600 }}>↑ {kpis.revenue.delta}%</span>
                          <div style={{ display:"flex", alignItems:"center", gap:10, marginLeft:"auto" }}>
                            <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                              <div style={{ width:8,height:2,background:C.or,borderRadius:1 }}/><span style={{ fontSize:10,color:C.text3 }}>Current</span>
                            </div>
                            <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                              <div style={{ width:8,height:2,background:"rgba(255,255,255,0.25)",borderRadius:1,backgroundImage:"repeating-linear-gradient(90deg,rgba(255,255,255,0.3) 0,rgba(255,255,255,0.3) 4px,transparent 4px,transparent 7px)" }}/><span style={{ fontSize:10,color:C.text3 }}>Previous</span>
                            </div>
                          </div>
                        </div>
                        <AreaChart data={revData} prev={prevData} color={C.or} prevColor="rgba(255,255,255,0.3)" h={170}/>
                      </div>
                    )}
                    {chartTab==="quotes" && (
                      <div className="an-fade" key="qt">
                        <div style={{ display:"flex", gap:20, marginBottom:12 }}>
                          <div>
                            <div style={{ fontSize:10,color:C.blue,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:2 }}>Submitted</div>
                            <div style={{ fontSize:22,fontWeight:700,color:C.text }}>{kpis.quotes.value}</div>
                          </div>
                          <div style={{ width:1,background:"var(--border)" }}/>
                          <div>
                            <div style={{ fontSize:10,color:C.or,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:2 }}>Approved</div>
                            <div style={{ fontSize:22,fontWeight:700,color:C.text }}>{Math.round(kpis.quotes.value*0.27)}</div>
                          </div>
                          <div style={{ width:1,background:"var(--border)" }}/>
                          <div>
                            <div style={{ fontSize:10,color:C.text3,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:2 }}>Conv. Rate</div>
                            <div style={{ fontSize:22,fontWeight:700,color:C.text }}>27%</div>
                          </div>
                        </div>
                        <BarChart data={qData} colorA={C.blue} colorB={C.or} labelA="Submitted" labelB="Approved" h={170} valueType="number"/>
                        <div style={{ display:"flex",gap:12,marginTop:8 }}>
                          <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:8,height:8,borderRadius:1,background:C.blue }}/><span style={{ fontSize:10,color:C.text3 }}>Submitted</span></div>
                          <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:8,height:8,borderRadius:1,background:C.or }}/><span style={{ fontSize:10,color:C.text3 }}>Approved</span></div>
                        </div>
                      </div>
                    )}
                    {chartTab==="invoices" && (
                      <div className="an-fade" key="inv">
                        <div style={{ display:"flex", gap:20, marginBottom:12 }}>
                          <div>
                            <div style={{ fontSize:10,color:C.blue,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:2 }}>Issued</div>
                            <div style={{ fontSize:22,fontWeight:700,color:C.text }}>{fmtCurrency(invData.reduce((s,d)=>s+d.a,0),true)}</div>
                          </div>
                          <div style={{ width:1,background:"var(--border)" }}/>
                          <div>
                            <div style={{ fontSize:10,color:C.green,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:2 }}>Collected</div>
                            <div style={{ fontSize:22,fontWeight:700,color:C.text }}>{fmtCurrency(invData.reduce((s,d)=>s+d.b,0),true)}</div>
                          </div>
                        </div>
                        <BarChart data={invData} colorA={C.blue} colorB={C.green} labelA="Issued" labelB="Paid" h={170} valueType="currency"/>
                        <div style={{ display:"flex",gap:12,marginTop:8 }}>
                          <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:8,height:8,borderRadius:1,background:C.blue }}/><span style={{ fontSize:10,color:C.text3 }}>Issued</span></div>
                          <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:8,height:8,borderRadius:1,background:C.green }}/><span style={{ fontSize:10,color:C.text3 }}>Collected</span></div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Quote funnel */}
                <Card>
                  <CardHead title="Quote Funnel" sub="Request → conversion"/>
                  <div style={{ padding:"16px 20px" }}>
                    {QUOTE_PIPELINE.map((step, i) => (
                      <div key={step.status} style={{ marginBottom:10, animation:`fadeUp 0.35s ease ${i*0.06}s both` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <span style={{ fontSize:11, color:C.text2 }}>{step.status}</span>
                          <div style={{ display:"flex", gap:8 }}>
                            <span style={{ fontSize:11, fontWeight:600, color:step.color }}>{step.count}</span>
                            {step.value > 0 && <span style={{ fontSize:10, color:C.text3 }}>{fmtCurrency(step.value,true)}</span>}
                          </div>
                        </div>
                        <div style={{ height:7, background:"rgba(255,255,255,0.06)", borderRadius:4, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${pct(step.count, QUOTE_PIPELINE[0].count)*100/100}%`, background:step.color, borderRadius:4, opacity:0.85, transition:"width 0.6s ease", maxWidth:"100%" }}/>
                        </div>
                        {i < QUOTE_PIPELINE.length-1 && (
                          <div style={{ fontSize:9, color:C.text3, marginTop:3, textAlign:"right" }}>
                            {pct(QUOTE_PIPELINE[i+1].count, step.count)}% → next
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ marginTop:14, paddingTop:12, borderTop:"1px solid var(--border)" }}>
                      <div style={{ fontSize:9, color:C.text3, textTransform:"uppercase", letterSpacing:"0.7px", marginBottom:5 }}>Overall conversion</div>
                      <div style={{ fontSize:22, fontWeight:700, color:C.text }}>
                        {pct(QUOTE_PIPELINE[QUOTE_PIPELINE.length-1].count, QUOTE_PIPELINE[0].count)}%
                        <span style={{ fontSize:11, fontWeight:400, color:C.text3, marginLeft:7 }}>request → order</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Bottom 3-col grid */}
              <div className="an-bot" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>

                {/* Geographic */}
                <Card>
                  <CardHead title="Geographic Breakdown" sub="Revenue by market"/>
                  <div style={{ padding:"12px 20px" }}>
                    {GEO_DATA.map((row, i) => (
                      <div key={row.country} style={{ padding:"8px 0", borderBottom:i<GEO_DATA.length-1?"1px solid rgba(255,255,255,0.04)":"none", animation:`fadeUp 0.3s ease ${i*0.05}s both` }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                          <span style={{ fontSize:16, flexShrink:0 }}>{row.flag}</span>
                          <span style={{ fontSize:12, fontWeight:500, color:C.text, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.country}</span>
                          <span style={{ fontSize:12, fontWeight:600, color:C.text, flexShrink:0 }}>{fmtCurrency(row.revenue,true)}</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ flex:1, height:3, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
                            <div style={{ height:"100%", width:`${row.pct}%`, background:C.or, opacity:0.75, borderRadius:2, transition:"width 0.6s ease" }}/>
                          </div>
                          <span style={{ fontSize:10, color:C.text3, flexShrink:0, width:28, textAlign:"right" }}>{row.pct}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Invoice breakdown + cert status */}
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                  <Card>
                    <CardHead title="Invoice Status"/>
                    <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:18 }}>
                      <DonutChart size={150} label="invoices" segments={[
                        { label:"Paid",      value:5,  color:C.green  },
                        { label:"Sent",      value:4,  color:C.blue   },
                        { label:"Overdue",   value:2,  color:C.red    },
                        { label:"Draft",     value:2,  color:C.text3  },
                        { label:"Other",     value:2,  color:C.amber  },
                      ]}/>
                      <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                        {[
                          { label:"Paid",    count:5,  color:C.green,  sub:"$64,445" },
                          { label:"Sent",    count:4,  color:C.blue,   sub:"$39,440" },
                          { label:"Overdue", count:2,  color:C.red,    sub:"$13,625" },
                          { label:"Draft",   count:2,  color:C.text3,  sub:"Not sent"},
                        ].map(s => (
                          <div key={s.label} style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <div style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }}/>
                            <span style={{ fontSize:11, color:C.text2, flex:1 }}>{s.label}</span>
                            <span style={{ fontSize:11, fontWeight:600, color:C.text }}>{s.count}</span>
                            <span style={{ fontSize:10, color:C.text3, minWidth:46, textAlign:"right" }}>{s.sub}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                  <Card>
                    <CardHead title="Certifications"/>
                    <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:18 }}>
                      <DonutChart size={150} label="certs" segments={[
                        { label:"Active",  value:11, color:C.green },
                        { label:"Pending", value:2,  color:C.amber },
                        { label:"Expired", value:3,  color:C.red   },
                      ]}/>
                      <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                        {[
                          { label:"Active",  count:11, color:C.green, sub:"69%" },
                          { label:"Pending", count:2,  color:C.amber, sub:"12%" },
                          { label:"Expired", count:3,  color:C.red,   sub:"19%" },
                        ].map(s => (
                          <div key={s.label} style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <div style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }}/>
                            <span style={{ fontSize:11, color:C.text2, flex:1 }}>{s.label}</span>
                            <span style={{ fontSize:11, fontWeight:600, color:C.text }}>{s.count}</span>
                            <span style={{ fontSize:10, color:C.text3 }}>{s.sub}</span>
                          </div>
                        ))}
                        <div style={{ paddingTop:6, borderTop:"1px solid var(--border)", marginTop:2 }}>
                          <span style={{ fontSize:10, color:C.amber }}>⚠ 2 expiring within 90 days</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Recent activity */}
                <Card>
                  <CardHead title="Recent Activity" sub="Last 7 days"/>
                  <div style={{ padding:"12px 16px" }}>
                    {RECENT_ACTIVITY.map((item, i) => (
                      <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"9px 0", borderBottom:i<RECENT_ACTIVITY.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                        <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>{item.icon}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:11, fontWeight:500, color:C.text2, lineHeight:1.4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.action}</div>
                          <div style={{ fontSize:10, color:C.text3, marginTop:2 }}>{item.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Invoice aging + product summary */}
              <div className="an-bot" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <Card>
                  <CardHead title="Invoice Aging" sub="Outstanding receivables by age"/>
                  <div style={{ padding:"16px 20px" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                      <div style={{ fontSize:22, fontWeight:700, color:C.text }}>{fmtCurrency(INVOICE_AGING.reduce((s,r)=>s+r.amount,0))}</div>
                      <div style={{ fontSize:11, color:C.text3 }}>total outstanding</div>
                    </div>
                    {INVOICE_AGING.map((row, i) => (
                      <div key={row.bucket} style={{ marginBottom:12, animation:`fadeUp 0.3s ease ${i*0.07}s both` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <span style={{ fontSize:12, fontWeight:500, color:C.text2 }}>{row.bucket}</span>
                          <div style={{ display:"flex", gap:12 }}>
                            <span style={{ fontSize:11, color:C.text3 }}>{row.count} inv.</span>
                            <span style={{ fontSize:12, fontWeight:600, color:row.color }}>{fmtCurrency(row.amount)}</span>
                          </div>
                        </div>
                        <HBar value={row.amount} max={INVOICE_AGING[0].amount} color={row.color}/>
                      </div>
                    ))}
                    <div style={{ marginTop:14, paddingTop:12, borderTop:"1px solid var(--border)" }}>
                      <div style={{ display:"flex", justifyContent:"space-between" }}>
                        <span style={{ fontSize:11, color:C.red }}>⚠ {fmtCurrency(INVOICE_AGING.find(r=>r.bucket==="60+ days")?.amount||0)} at risk (60+ days)</span>
                        <span style={{ fontSize:11, color:C.text3 }}>12 total invoices</span>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <CardHead title="Storage & Files" sub="24 files · 1 GB quota"/>
                  <div style={{ padding:"16px 20px" }}>
                    <div style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:7 }}>
                        <span style={{ fontSize:12, color:C.text2 }}>405 MB used</span>
                        <span style={{ fontSize:12, color:C.text3 }}>40% of 1 GB</span>
                      </div>
                      <div style={{ height:7, background:"rgba(255,255,255,0.06)", borderRadius:4, overflow:"hidden", display:"flex" }}>
                        {[
                          { label:"Images",       value:9, color:C.green  },
                          { label:"Documents",    value:7, color:C.blue   },
                          { label:"Videos",       value:2, color:C.purple },
                          { label:"Archives",     value:3, color:C.amber  },
                          { label:"Spreadsheets", value:3, color:C.or     },
                        ].map(f => (
                          <div key={f.label} style={{ height:"100%", flex:f.value, background:f.color, opacity:0.82, transition:"flex 0.5s" }}/>
                        ))}
                      </div>
                    </div>
                    {[
                      { label:"Images",       files:9,  size:"11.8 MB", color:C.green  },
                      { label:"Documents",    files:7,  size:"34.3 MB", color:C.blue   },
                      { label:"Videos",       files:2,  size:"202 MB",  color:C.purple },
                      { label:"Archives",     files:3,  size:"155 MB",  color:C.amber  },
                      { label:"Spreadsheets", files:3,  size:"2.1 MB",  color:C.or     },
                    ].map((f, i) => (
                      <div key={f.label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7, animation:`fadeUp 0.3s ease ${i*0.05}s both` }}>
                        <div style={{ width:6, height:6, borderRadius:1, background:f.color, flexShrink:0 }}/>
                        <span style={{ fontSize:11, color:C.text2, flex:1 }}>{f.label}</span>
                        <span style={{ fontSize:11, color:C.text3 }}>{f.files} files</span>
                        <span style={{ fontSize:11, fontWeight:500, color:C.text, minWidth:50, textAlign:"right" }}>{f.size}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* ────────────────────── REVENUE PAGE ────────────────────── */}
          {activePage === "revenue" && (
            <div className="an-fade" key={`revenue-${animKey}`}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
                {[
                  { label:"Total Revenue",   value:fmtCurrency(kpis.revenue.value),      dlt:kpis.revenue.delta,     icon:"💰", color:C.or,     spark:spark.rev  },
                  { label:"Outstanding",     value:fmtCurrency(kpis.outstanding.value),  dlt:kpis.outstanding.delta, icon:"⏳", color:C.amber,  spark:spark.out  },
                  { label:"Avg Deal",        value:fmtCurrency(kpis.avgDeal.value),      dlt:kpis.avgDeal.delta,     icon:"📈", color:C.green,  spark:spark.deal },
                  { label:"Invoices Issued", value:String(kpis.invoices.value),          dlt:kpis.invoices.delta,    icon:"🧾", color:C.blue,   spark:spark.inv  },
                ].map((k, i) => (
                  <div key={k.label} className="an-up" style={{ animationDelay:`${i*0.05}s` }}>
                    <KpiCard {...k} prevValue={undefined}/>
                  </div>
                ))}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
                <Card>
                  <CardHead title="Revenue vs Previous Period" sub={`${dateRange} comparison`}/>
                  <div style={{ padding:"16px 20px 12px" }}>
                    <AreaChart data={revData} prev={prevData} color={C.or} prevColor="rgba(255,255,255,0.25)" h={200}/>
                    <div style={{ display:"flex", gap:16, marginTop:10 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:12,height:2,background:C.or,borderRadius:1 }}/><span style={{ fontSize:10,color:C.text3 }}>Current</span></div>
                      <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:12,height:2,background:"rgba(255,255,255,0.3)",borderRadius:1 }}/><span style={{ fontSize:10,color:C.text3 }}>Previous</span></div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <CardHead title="Invoice Aging"/>
                  <div style={{ padding:"14px 20px" }}>
                    {INVOICE_AGING.map((r, i) => (
                      <div key={r.bucket} style={{ marginBottom:12, animation:`fadeUp 0.3s ease ${i*0.07}s both` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <span style={{ fontSize:11, color:C.text2 }}>{r.bucket}</span>
                          <span style={{ fontSize:12, fontWeight:600, color:r.color }}>{fmtCurrency(r.amount)}</span>
                        </div>
                        <HBar value={r.amount} max={INVOICE_AGING[0].amount} color={r.color}/>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              <Card>
                <CardHead title="Invoice vs Payment Timeline" sub="Issued vs collected per period"/>
                <div style={{ padding:"16px 20px 12px" }}>
                  <BarChart data={invData} colorA={C.blue} colorB={C.green} labelA="Issued" labelB="Paid" h={180} valueType="currency"/>
                  <div style={{ display:"flex",gap:14,marginTop:10 }}>
                    <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:8,height:8,borderRadius:1,background:C.blue }}/><span style={{ fontSize:10,color:C.text3 }}>Issued</span></div>
                    <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:8,height:8,borderRadius:1,background:C.green }}/><span style={{ fontSize:10,color:C.text3 }}>Collected</span></div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ────────────────────── PRODUCTS PAGE ────────────────────── */}
          {activePage === "products" && (
            <div className="an-fade" key={`products-${animKey}`}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
                {[
                  { label:"Total Products",    value:"20",  dlt:0,    icon:"📦", color:C.blue,   spark:Array.from({length:12},(_,i)=>Math.round(18+sr(i+90)()*4)) },
                  { label:"Total Page Views",  value:"9.4K",dlt:+12,  icon:"👁", color:C.purple, spark:Array.from({length:12},(_,i)=>Math.round(700+sr(i+91)()*500)) },
                  { label:"Quote Requests",    value:"24",  dlt:+8.7, icon:"📋", color:C.or,     spark:spark.q },
                  { label:"Product Revenue",   value:"$169K",dlt:+14.2,icon:"💰",color:C.green,  spark:spark.rev },
                ].map((k, i) => (
                  <div key={k.label} className="an-up" style={{ animationDelay:`${i*0.05}s` }}>
                    <KpiCard {...k} prevValue={undefined}/>
                  </div>
                ))}
              </div>
              <Card style={{ marginBottom:16 }}>
                <CardHead title="Product Performance" sub={`Revenue, engagement and quote activity · ${PRODUCT_ROWS.length} products`}/>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", minWidth:700 }}>
                    <thead>
                      <tr style={{ background:"rgba(255,255,255,0.02)" }}>
                        {["#","Product","Category","Page Views","Quotes","Revenue","30d Trend","Status"].map((h, hi) => (
                          <th key={h} style={{
                            padding:"10px 14px", textAlign:hi>2?"right":"left",
                            borderBottom:"1px solid var(--border)",
                            fontSize:10, fontWeight:600, color:C.text3,
                            textTransform:"uppercase", letterSpacing:"0.7px", whiteSpace:"nowrap",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {PRODUCT_ROWS.map((p, i) => (
                        <tr key={p.name} className="an-row"
                          style={{ borderBottom:i<PRODUCT_ROWS.length-1?"1px solid rgba(255,255,255,0.04)":"none", transition:"background 0.12s", animation:`fadeUp 0.25s ease ${i*0.04}s both` }}>
                          <td style={{ padding:"10px 14px", fontSize:11, fontWeight:700, color:C.or }}>{i+1}</td>
                          <td style={{ padding:"10px 14px" }}>
                            <div style={{ fontSize:12, fontWeight:500, color:C.text }}>{p.name}</div>
                            <div style={{ fontSize:10, color:C.text3, fontFamily:"monospace" }}>{p.sku}</div>
                          </td>
                          <td style={{ padding:"10px 14px" }}>
                            <span style={{ fontSize:10, color:C.text3, background:"rgba(255,255,255,0.05)", border:"1px solid var(--border)", borderRadius:4, padding:"2px 7px" }}>{p.category}</span>
                          </td>
                          <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, color:C.text2, fontVariantNumeric:"tabular-nums" }}>{fmtNum(p.views)}</td>
                          <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, color:C.text2 }}>{p.quotes}</td>
                          <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, fontWeight:600, color:C.text, fontVariantNumeric:"tabular-nums" }}>{fmtCurrency(p.revenue)}</td>
                          <td style={{ padding:"10px 14px", textAlign:"right" }}>
                            <div style={{ display:"flex", justifyContent:"flex-end" }}>
                              <Sparkline data={p.trend} color={C.or} w={72} h={26}/>
                            </div>
                          </td>
                          <td style={{ padding:"10px 14px", textAlign:"right" }}>
                            <span style={{ fontSize:10, fontWeight:600, color:"#6ddc6d", background:"rgba(109,220,109,0.10)", border:"1px solid rgba(109,220,109,0.25)", borderRadius:20, padding:"2px 8px" }}>Live</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <Card>
                  <CardHead title="Revenue by Category"/>
                  <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:20 }}>
                    <DonutChart size={100} label="categories" segments={[
                      { label:"Surveying",   value:4, color:C.or     },
                      { label:"Instruments", value:3, color:C.blue   },
                      { label:"Accessories", value:2, color:C.green  },
                      { label:"Apparel",     value:1, color:C.purple },
                      { label:"Safety",      value:1, color:C.amber  },
                    ]}/>
                    <div style={{ flex:1, display:"flex", flexDirection:"column", gap:7 }}>
                      {[
                        { label:"Surveying",   pct:45, color:C.or     },
                        { label:"Instruments", pct:30, color:C.blue   },
                        { label:"Accessories", pct:13, color:C.green  },
                        { label:"Apparel",     pct:7,  color:C.purple },
                        { label:"Safety",      pct:5,  color:C.amber  },
                      ].map(s => (
                        <div key={s.label} style={{ display:"flex", alignItems:"center", gap:7 }}>
                          <div style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }}/>
                          <span style={{ fontSize:11, color:C.text2, flex:1 }}>{s.label}</span>
                          <span style={{ fontSize:11, fontWeight:600, color:C.text }}>{s.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
                <Card>
                  <CardHead title="Top Products by Views"/>
                  <div style={{ padding:"12px 20px" }}>
                    {PRODUCT_ROWS.slice(0,6).map((p, i) => (
                      <div key={p.name} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 0", borderBottom:i<5?"1px solid rgba(255,255,255,0.04)":"none" }}>
                        <span style={{ fontSize:10, fontWeight:700, color:C.or, width:16, flexShrink:0 }}>#{i+1}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:11, fontWeight:500, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:3 }}>{p.name}</div>
                          <HBar value={p.views} max={PRODUCT_ROWS[0].views} color={C.or}/>
                        </div>
                        <span style={{ fontSize:11, color:C.text3, flexShrink:0 }}>{fmtNum(p.views)}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* ────────────────────── QUOTES PAGE ────────────────────── */}
          {activePage === "quotes" && (
            <div className="an-fade" key={`quotes-${animKey}`}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
                {[
                  { label:"Total Requests",  value:String(kpis.quotes.value), dlt:kpis.quotes.delta,    icon:"📋", color:C.blue,   spark:spark.q },
                  { label:"Conversion Rate", value:"20%",                      dlt:kpis.convRate.delta,  icon:"🎯", color:C.green,  spark:spark.conv },
                  { label:"Avg Deal Value",  value:fmtCurrency(5580,true),     dlt:kpis.avgDeal.delta,   icon:"💰", color:C.or,     spark:spark.deal },
                  { label:"Pipeline Value",  value:"$23.7K",                   dlt:+11.2,                icon:"📈", color:C.purple, spark:Array.from({length:12},(_,i)=>18000+sr(i+70)()*12000) },
                ].map((k, i) => (
                  <div key={k.label} className="an-up" style={{ animationDelay:`${i*0.05}s` }}>
                    <KpiCard {...k} prevValue={undefined}/>
                  </div>
                ))}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16, marginBottom:16 }}>
                <Card>
                  <CardHead title="Quote Activity" sub="Submitted vs Approved over time"/>
                  <div style={{ padding:"16px 20px 12px" }}>
                    <BarChart data={qData} colorA={C.blue} colorB={C.or} labelA="Submitted" labelB="Approved" h={190} valueType="number"/>
                    <div style={{ display:"flex",gap:14,marginTop:10 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:8,height:8,borderRadius:1,background:C.blue }}/><span style={{ fontSize:10,color:C.text3 }}>Submitted</span></div>
                      <div style={{ display:"flex",alignItems:"center",gap:5 }}><div style={{ width:8,height:8,borderRadius:1,background:C.or }}/><span style={{ fontSize:10,color:C.text3 }}>Approved</span></div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <CardHead title="Pipeline Status"/>
                  <div style={{ padding:"14px 20px" }}>
                    {QUOTE_PIPELINE.map((s, i) => (
                      <div key={s.status} style={{ marginBottom:12, animation:`fadeUp 0.3s ease ${i*0.06}s both` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <span style={{ fontSize:11, color:C.text2 }}>{s.status}</span>
                          <div style={{ display:"flex", gap:8 }}>
                            <span style={{ fontSize:11, fontWeight:600, color:s.color }}>{s.count}</span>
                            {s.value > 0 && <span style={{ fontSize:10, color:C.text3 }}>{fmtCurrency(s.value,true)}</span>}
                          </div>
                        </div>
                        <HBar value={s.count} max={QUOTE_PIPELINE[0].count} color={s.color}/>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <Card>
                  <CardHead title="Requests by Country"/>
                  <div style={{ padding:"12px 20px" }}>
                    {GEO_DATA.map((row, i) => (
                      <div key={row.country} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:i<GEO_DATA.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                        <span style={{ fontSize:16 }}>{row.flag}</span>
                        <span style={{ fontSize:12, fontWeight:500, color:C.text, flex:1 }}>{row.country}</span>
                        <div style={{ display:"flex", gap:14, flexShrink:0 }}>
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:9,color:C.text3,marginBottom:1 }}>Quotes</div>
                            <div style={{ fontSize:12,fontWeight:600,color:C.blue }}>{row.quotes}</div>
                          </div>
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:9,color:C.text3,marginBottom:1 }}>Revenue</div>
                            <div style={{ fontSize:12,fontWeight:600,color:C.or }}>{fmtCurrency(row.revenue,true)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card>
                  <CardHead title="Sample Type Breakdown"/>
                  <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:20 }}>
                    <DonutChart size={100} label="requests" segments={[
                      { label:"Physical",   value:9,  color:C.or     },
                      { label:"Test Unit",  value:3,  color:C.blue   },
                      { label:"Demo Kit",   value:2,  color:C.purple },
                      { label:"Digital",    value:1,  color:C.green  },
                    ]}/>
                    <div style={{ flex:1, display:"flex", flexDirection:"column", gap:7 }}>
                      {[
                        { label:"Physical sample", count:9,  pct:60, color:C.or     },
                        { label:"Test unit",       count:3,  pct:20, color:C.blue   },
                        { label:"Demo kit",        count:2,  pct:13, color:C.purple },
                        { label:"Digital spec",    count:1,  pct:7,  color:C.green  },
                      ].map(s => (
                        <div key={s.label} style={{ display:"flex", alignItems:"center", gap:7 }}>
                          <div style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }}/>
                          <span style={{ fontSize:11, color:C.text2, flex:1 }}>{s.label}</span>
                          <span style={{ fontSize:11, fontWeight:600, color:C.text }}>{s.count}</span>
                          <span style={{ fontSize:10, color:C.text3, width:28, textAlign:"right" }}>{s.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* ────────────────────── CONTENT PAGE ────────────────────── */}
          {activePage === "content" && (
            <div className="an-fade" key={`content-${animKey}`}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
                {[
                  { label:"Total Products",       value:"20",   dlt:0,    icon:"📦", color:C.blue,   spark:Array.from({length:12},(_,i)=>Math.round(18+sr(i+80)()*3)) },
                  { label:"Active Certifications", value:"11",  dlt:+2,   icon:"✅", color:C.green,  spark:Array.from({length:12},(_,i)=>Math.round(9+sr(i+81)()*3))  },
                  { label:"Total Files",           value:"24",  dlt:+8,   icon:"📁", color:C.amber,  spark:Array.from({length:12},(_,i)=>Math.round(18+sr(i+82)()*8))  },
                  { label:"Total Page Views",      value:"9.4K",dlt:+12,  icon:"👁", color:C.purple, spark:Array.from({length:12},(_,i)=>Math.round(700+sr(i+83)()*500))},
                ].map((k, i) => (
                  <div key={k.label} className="an-up" style={{ animationDelay:`${i*0.05}s` }}>
                    <KpiCard {...k} prevValue={undefined}/>
                  </div>
                ))}
              </div>
              <Card style={{ marginBottom:16 }}>
                <CardHead title="Content Health" sub="Recently updated items across all sections"/>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", minWidth:600 }}>
                    <thead>
                      <tr style={{ background:"rgba(255,255,255,0.02)" }}>
                        {["Type","Name","Status","Last Updated","Page Views","Action"].map((h, hi) => (
                          <th key={h} style={{ padding:"10px 14px", textAlign:hi>3?"right":"left", borderBottom:"1px solid var(--border)", fontSize:10, fontWeight:600, color:C.text3, textTransform:"uppercase", letterSpacing:"0.7px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {CONTENT_ITEMS.map((item, i) => (
                        <tr key={item.name} className="an-row"
                          style={{ borderBottom:i<CONTENT_ITEMS.length-1?"1px solid rgba(255,255,255,0.04)":"none", transition:"background 0.12s", animation:`fadeUp 0.25s ease ${i*0.04}s both` }}>
                          <td style={{ padding:"10px 14px" }}>
                            <span style={{ fontSize:10, background:"rgba(255,255,255,0.05)", border:"1px solid var(--border)", borderRadius:4, padding:"2px 7px", color:C.text3 }}>{item.type}</span>
                          </td>
                          <td style={{ padding:"10px 14px", fontSize:12, fontWeight:500, color:C.text }}>{item.name}</td>
                          <td style={{ padding:"10px 14px" }}>
                            <span style={{
                              fontSize:10, fontWeight:600,
                              color:item.status==="published"?"#6ddc6d":"#f5a623",
                              background:item.status==="published"?"rgba(109,220,109,0.10)":"rgba(245,166,35,0.10)",
                              border:`1px solid ${item.status==="published"?"rgba(109,220,109,0.25)":"rgba(245,166,35,0.25)"}`,
                              borderRadius:20, padding:"2px 8px", textTransform:"capitalize",
                            }}>{item.status}</span>
                          </td>
                          <td style={{ padding:"10px 14px", fontSize:11, color:C.text3 }}>{item.updated}</td>
                          <td style={{ padding:"10px 14px", textAlign:"right", fontSize:11, color:C.text2, fontVariantNumeric:"tabular-nums" }}>{fmtNum(item.views)}</td>
                          <td style={{ padding:"10px 14px", textAlign:"right" }}>
                            <button style={{ fontSize:11, padding:"4px 10px", borderRadius:6, border:"1px solid rgba(191,61,12,0.3)", background:"var(--or-dim)", color:"var(--or)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
                <Card>
                  <CardHead title="Products by Status"/>
                  <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:20 }}>
                    <DonutChart size={90} label="products" segments={[
                      { label:"Published", value:14, color:C.green  },
                      { label:"Draft",     value:4,  color:C.amber  },
                      { label:"Archived",  value:2,  color:C.text3  },
                    ]}/>
                    <div style={{ flex:1, display:"flex", flexDirection:"column", gap:7 }}>
                      {[
                        { label:"Published", value:14, color:C.green },
                        { label:"Draft",     value:4,  color:C.amber },
                        { label:"Archived",  value:2,  color:C.text3 },
                      ].map(s => (
                        <div key={s.label} style={{ display:"flex", alignItems:"center", gap:7 }}>
                          <div style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }}/>
                          <span style={{ fontSize:11, color:C.text2, flex:1 }}>{s.label}</span>
                          <span style={{ fontSize:11, fontWeight:600, color:C.text }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
                <Card>
                  <CardHead title="Certifications"/>
                  <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", gap:20 }}>
                    <DonutChart size={90} label="certs" segments={[
                      { label:"Active",  value:11, color:C.green },
                      { label:"Pending", value:2,  color:C.amber },
                      { label:"Expired", value:3,  color:C.red   },
                    ]}/>
                    <div style={{ flex:1, display:"flex", flexDirection:"column", gap:7 }}>
                      {[
                        { label:"Active",  count:11, color:C.green },
                        { label:"Pending", count:2,  color:C.amber },
                        { label:"Expired", count:3,  color:C.red   },
                      ].map(s => (
                        <div key={s.label} style={{ display:"flex", alignItems:"center", gap:7 }}>
                          <div style={{ width:6, height:6, borderRadius:"50%", background:s.color, flexShrink:0 }}/>
                          <span style={{ fontSize:11, color:C.text2, flex:1 }}>{s.label}</span>
                          <span style={{ fontSize:11, fontWeight:600, color:C.text }}>{s.count}</span>
                        </div>
                      ))}
                      <div style={{ fontSize:10, color:C.amber, paddingTop:6, borderTop:"1px solid var(--border)" }}>
                        ⚠ 2 expiring soon
                      </div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <CardHead title="File Storage"/>
                  <div style={{ padding:"14px 20px" }}>
                    <div style={{ height:6, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden", display:"flex", marginBottom:12 }}>
                      {[{c:C.green,v:9},{c:C.blue,v:7},{c:C.purple,v:2},{c:C.amber,v:3},{c:C.or,v:3}].map((f,i) => (
                        <div key={i} style={{ flex:f.v, background:f.c, opacity:0.82 }}/>
                      ))}
                    </div>
                    {[
                      { label:"Images",       files:9,  size:"11.8 MB", color:C.green  },
                      { label:"Documents",    files:7,  size:"34.3 MB", color:C.blue   },
                      { label:"Videos",       files:2,  size:"202 MB",  color:C.purple },
                      { label:"Archives",     files:3,  size:"155 MB",  color:C.amber  },
                      { label:"Spreadsheets", files:3,  size:"2.1 MB",  color:C.or     },
                    ].map((f, i) => (
                      <div key={f.label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                        <div style={{ width:6, height:6, borderRadius:1, background:f.color, flexShrink:0 }}/>
                        <span style={{ fontSize:11, color:C.text2, flex:1 }}>{f.label}</span>
                        <span style={{ fontSize:10, color:C.text3 }}>{f.files}</span>
                        <span style={{ fontSize:11, fontWeight:500, color:C.text, minWidth:50, textAlign:"right" }}>{f.size}</span>
                      </div>
                    ))}
                    <div style={{ fontSize:10, color:C.text3, paddingTop:8, borderTop:"1px solid var(--border)", marginTop:4 }}>
                      405 MB of 1 GB used · 40%
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}